import {
    CheckCircleFilled,
    ClockCircleOutlined,
    CloseCircleFilled,
    DownloadOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Empty, message, Progress, Space, Table, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { receiptAPIWithErrorHandling } from "../../../api/receipt/receipt";
import { downloadBlob } from "../../../utils/excel";
import { neutral, semantic, spacing } from "../../../theme/tokens";

const { Text } = Typography;

/** Nhịp làm mới khi còn lượt đang chạy. Hết việc thì dừng hẳn, không poll vô ích. */
const POLL_INTERVAL_MS = 4000;

const ACTIVE_STATUSES = ["queued", "processing"];

/** Khớp với --days của lệnh exports:clear-old. Chỉ dùng để hiển thị. */
const RETENTION_DAYS = 7;

/**
 * Lịch sử xuất phiếu thu của một đợt thu.
 *
 * Xuất phiếu chạy ở hàng đợi nên không còn trả file ngay trong request. Đây là chỗ người
 * vận hành xem lượt nào đang chạy tới đâu và tải lại file của những lượt đã xong.
 */
const ModalHistoryReceiptExport = ({ open, onClose, rootId }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);
    const timerRef = useRef(null);

    const loadData = useCallback(
        async ({ silent = false } = {}) => {
            if (!rootId) return;

            try {
                if (!silent) setLoading(true);
                const res = await receiptAPIWithErrorHandling.getExportHistories(rootId);
                setData(res?.data || []);
            } catch (error) {
                // Lần làm mới ở nền thất bại thì im lặng, không đẩy toast trong lúc người
                // ta đang đọc bảng.
                if (!silent) message.error(error.message);
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [rootId]
    );

    // Chỉ hẹn lượt làm mới tiếp theo khi vẫn còn lượt xuất đang chạy.
    useEffect(() => {
        clearTimeout(timerRef.current);

        if (!open) return undefined;

        const hasRunning = data.some((item) => ACTIVE_STATUSES.includes(item.status));
        if (!hasRunning) return undefined;

        timerRef.current = setTimeout(() => loadData({ silent: true }), POLL_INTERVAL_MS);

        return () => clearTimeout(timerRef.current);
    }, [open, data, loadData]);

    useEffect(() => {
        if (open) loadData();
        return () => clearTimeout(timerRef.current);
    }, [open, loadData]);

    const handleDownload = async (record) => {
        const messageKey = `receipt-export-${record.id}`;
        try {
            setDownloadingId(record.id);
            message.loading({ key: messageKey, content: "Đang tải file..." });

            const blob = await receiptAPIWithErrorHandling.downloadExport(record.id);
            const isZip = String(record.file_path || "").endsWith(".zip");
            const ext = isZip ? "zip" : "pdf";
            const stamp = dayjs(record.created_at).format("YYYYMMDD_HHmm");

            downloadBlob(
                new Blob([blob], { type: isZip ? "application/zip" : "application/pdf" }),
                `phieu-thu_${record.label}_${stamp}.${ext}`
            );

            message.success({ key: messageKey, content: "Tải file thành công", duration: 3 });
        } catch (error) {
            message.error({ key: messageKey, content: error.message, duration: 4 });
        } finally {
            setDownloadingId(null);
        }
    };

    const renderStatus = (record) => {
        if (record.status === "queued") {
            // Cố tình KHÔNG hiện 0%: job còn xếp hàng, hiện 0% làm người ta tưởng treo.
            return (
                <Tag icon={<ClockCircleOutlined />} color="default">
                    Đang chờ
                </Tag>
            );
        }

        if (record.status === "processing") {
            return (
                <div style={{ minWidth: 120 }}>
                    <Progress percent={record.progress} size="small" status="active" />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.done + record.skipped}/{record.total} phiếu
                    </Text>
                </div>
            );
        }

        if (record.status === "completed") {
            return (
                <Tag icon={<CheckCircleFilled />} color="success">
                    Hoàn thành
                </Tag>
            );
        }

        return (
            <Tooltip title={record.error || "Không rõ nguyên nhân"}>
                <Tag icon={<CloseCircleFilled />} color="error">
                    Thất bại
                </Tag>
            </Tooltip>
        );
    };

    const columns = [
        {
            title: "Phạm vi",
            key: "label",
            render: (_, record) => (
                <div>
                    <Text strong>{record.label}</Text>
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.scope === "parent" ? "Xuất toàn bộ" : "Xuất một đơn vị"}
                        </Text>
                    </div>
                </div>
            ),
        },
        {
            title: "Số phiếu",
            key: "total",
            width: 130,
            render: (_, record) => (
                <div>
                    <Text>{record.total} phiếu</Text>
                    {record.skipped > 0 && (
                        <div>
                            <Tooltip title="Khoản thu chưa có mã thanh toán nên phiếu in ra không có QR">
                                <Text style={{ fontSize: 12, color: semantic.warning }}>
                                    {record.skipped} phiếu thiếu QR
                                </Text>
                            </Tooltip>
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Trạng thái",
            key: "status",
            width: 170,
            render: (_, record) => renderStatus(record),
        },
        {
            title: "Người xuất",
            key: "creator",
            width: 150,
            render: (_, record) => (
                <div>
                    <Text style={{ fontSize: 13 }}>{record.creator?.username || "—"}</Text>
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(record.created_at).format("DD/MM/YYYY HH:mm")}
                        </Text>
                    </div>
                </div>
            ),
        },
        {
            title: "Tải về",
            key: "action",
            width: 120,
            align: "center",
            render: (_, record) => {
                // Lệnh dọn định kỳ xóa file rồi gỡ luôn file_path, nên file_path rỗng ở một
                // lượt đã hoàn thành nghĩa là file đã hết hạn. Chặn ở đây để người dùng
                // không bấm rồi mới nhận lỗi.
                const expired = record.status === "completed" && !record.file_path;

                if (expired) {
                    return (
                        <Tooltip title={`File chỉ được giữ ${RETENTION_DAYS} ngày. Bấm Xuất phiếu thu để tạo bản mới với số tiền hiện tại.`}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Đã hết hạn
                            </Text>
                        </Tooltip>
                    );
                }

                return (
                    <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        disabled={record.status !== "completed"}
                        loading={downloadingId === record.id}
                        onClick={() => handleDownload(record)}
                    >
                        {record.file_size ? `${(record.file_size / 1048576).toFixed(1)} MB` : "Tải"}
                    </Button>
                );
            },
        },
    ];

    return (
        <Drawer
            title="Lịch sử xuất phiếu thu"
            width={880}
            open={open}
            onClose={onClose}
            extra={
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={() => loadData()} loading={loading}>
                        Làm mới
                    </Button>
                </Space>
            }
        >
            <Text type="secondary" style={{ display: "block", marginBottom: spacing[4] }}>
                50 lượt xuất gần nhất của đợt thu này. File được giữ {RETENTION_DAYS} ngày rồi hệ thống tự dọn —
                số tiền trên phiếu là số của lúc xuất, cần bản mới thì bấm Xuất phiếu thu lại.
            </Text>

            <Table
                rowKey="id"
                size="small"
                loading={loading}
                dataSource={data}
                columns={columns}
                pagination={false}
                scroll={{ y: "calc(100vh - 260px)" }}
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Chưa có lượt xuất phiếu thu nào"
                            style={{ color: neutral[500] }}
                        />
                    ),
                }}
            />
        </Drawer>
    );
};

export default ModalHistoryReceiptExport;
